/*
 * predictive-search-dedup.js — single-populated-instance guard for predictive search (issue #154)
 *
 * Additive, isolated guard. Does NOT modify assets/theme.js (a 433KB minified
 * bundle with no source in this repo); the clean fix belongs in the 8.1.1 source,
 * this is the interim 7.3 guard, in the same spirit as atc-guard.js.
 *
 * The problem (WCAG 4.1.2 / 1.3.1): two <predictive-search> instances live in the
 * DOM at all times — the mobile drawer (#search-drawer-results) and the desktop
 * header popdown (#search-popdown-results). Result option ids are FIXED strings
 * emitted by sections/predictive-search.liquid (predictive-search-option-query-N,
 * -product-N, -article-N, -page-N, -collection-N, -search-for). The theme's
 * renderSearchResults() sets one instance's container innerHTML, but closeResults()
 * only HIDES a populated instance — it never clears innerHTML (only reset(), on an
 * empty query, clears). So if a shopper searches in one instance and then the other
 * (most naturally by resizing across the breakpoint and searching again), BOTH
 * containers hold the same fixed ids. getElementById — which the theme's
 * aria-activedescendant flow relies on — then resolves to the first match in
 * document order (the drawer), so the popdown input's active option points at a
 * hidden node in the other instance's listbox. Confirmed live: two elements with
 * id="predictive-search-option-query-1", resolving to the drawer.
 *
 * The fix: enforce the invariant that at most one instance is ever populated. When
 * any instance's results container gains option children, keep the PRIMARY instance
 * — the one the shopper is actually using — and empty every other instance (innerHTML
 * plus the results/open state and the stale aria-activedescendant it left on its
 * input). One populated instance => the fixed ids are unique => aria-activedescendant
 * always resolves within the active listbox. Option-type agnostic: fixes all six
 * option types (and the search-for button) at once, and clears the
 * stale-markup-after-close footgun that is the root cause.
 *
 * "Primary" is the FOCUSED instance, else the VISIBLE one, and only as a last resort
 * the instance that just rendered. This matters because the trigger is any render,
 * including a delayed/stale response landing in the hidden instance: keying off
 * "last rendered" would let that stale response clear the results the shopper is
 * actively looking at (and its aria-activedescendant). Keying off focus/visibility
 * instead means a background render in the off-screen instance clears ITSELF, never
 * the active one.
 *
 * Safe by construction: the kept instance is the one in use; the two instances are
 * mutually exclusive by viewport, so any cleared one is off-screen. Clearing a
 * container fires the observer again, but with no options, so it is a no-op — no loop.
 * No-ops entirely when predictive search is disabled (no containers exist).
 */
(function () {
  'use strict';

  var INSTANCE = 'predictive-search';
  var CONTAINER = '[data-predictive-search-results]';
  var INPUT = '[data-predictive-search-input]';
  var OPTION = '[role="option"]';

  function clearInstance(instance) {
    var container = instance.querySelector(CONTAINER);
    if (container && container.innerHTML !== '') container.innerHTML = '';
    instance.removeAttribute('results');
    instance.removeAttribute('open');
    var input = instance.querySelector(INPUT);
    if (input) {
      input.setAttribute('aria-activedescendant', '');
      // Keep the combobox ARIA consistent: an emptied instance must not stay
      // exposed as expanded with no options (theme.js sets aria-expanded on open).
      input.setAttribute('aria-expanded', 'false');
      // PR #207: retargetAriaControls points aria-controls at the injected
      // listbox id; emptying the container deletes that element, so restore
      // aria-controls to the persistent container to avoid a dangling ref.
      if (container && container.id) input.setAttribute('aria-controls', container.id);
    }
  }

  function isFocused(instance) {
    var input = instance.querySelector(INPUT);
    return !!input && document.activeElement === input;
  }

  function isVisible(instance) {
    var container = instance.querySelector(CONTAINER);
    if (!container) return false;
    // Let the browser decide CSS visibility — this covers display:none,
    // content-visibility, visibility, AND opacity including an ANCESTOR's opacity,
    // which a container-only computed-style check misses. Fall back to explicit
    // checks where checkVisibility is unavailable.
    if (typeof container.checkVisibility === 'function') {
      if (!container.checkVisibility({
        opacityProperty: true,
        visibilityProperty: true,
        contentVisibilityAuto: true
      })) return false;
    } else {
      if (container.getClientRects().length === 0) return false; // display:none / no box
      var cs = window.getComputedStyle(container);
      if (cs.visibility === 'hidden' || cs.visibility === 'collapse') return false;
      if (parseFloat(cs.opacity) === 0) return false;
    }
    // checkVisibility does not consider off-canvas placement: require the box to
    // intersect the viewport so a positioned/transformed off-screen instance
    // (e.g. an off-canvas drawer) is never taken for the one the shopper is viewing.
    var r = container.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (r.right <= 0 || r.bottom <= 0 || r.left >= vw || r.top >= vh) return false;
    return true;
  }

  // The instance to keep populated: the one the shopper is using — focused first,
  // then visible, and only as a fallback the one that just rendered. Keying off the
  // just-rendered container instead would let a delayed/stale response in the hidden
  // instance wipe the active instance's visible results.
  function pickPrimary(justRendered) {
    var instances = [].slice.call(document.querySelectorAll(INSTANCE));
    var focused = instances.filter(isFocused)[0];
    if (focused) return focused;
    var visible = instances.filter(isVisible)[0];
    if (visible) return visible;
    return justRendered;
  }

  // A container just gained option children: empty every instance except the primary
  // so the fixed option ids exist in exactly one instance.
  function enforceSingle(renderedContainer) {
    var justRendered = renderedContainer.closest(INSTANCE);
    if (!justRendered) return;
    var primary = pickPrimary(justRendered);
    document.querySelectorAll(INSTANCE).forEach(function (instance) {
      if (instance !== primary) clearInstance(instance);
    });
  }

  function init() {
    var containers = document.querySelectorAll(CONTAINER);
    if (!containers.length) return; // predictive search off, or no instances

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var container = mutations[i].target.closest
          ? mutations[i].target.closest(CONTAINER)
          : null;
        // Only act when the container actually holds options (ignore the empty
        // state a clear leaves behind — that is what prevents an observer loop).
        if (container && container.querySelector(OPTION)) {
          enforceSingle(container);
          return;
        }
      }
    });

    containers.forEach(function (container) {
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
